import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { CalAngularService } from "@cvx/cal-angular";
import { GraphApplicationService } from "src/app/services/graph/GraphApplication.service";
import { IGenericApiResponse } from 'src/app/models/common/GenericApiResponse';
import { MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { MatPaginator } from "@angular/material/paginator";
import { SelectionModel } from "@angular/cdk/collections";
import { GraphApplication, IGraphApplication } from "src/app/models/graph/applications/GraphApplication";
import { IGraphAppExposedPermissions } from "src/app/models/graph/applications/GraphAppExposedPermissions";
import { GraphApplicationPermissions, IGraphApplicationPermissions } from "src/app/models/graph/applications/GraphApplicationPermissions";
import { GraphConstants } from "src/app/models/graph/Graph.constants";
import { AppRoleAllowedMemberType } from "src/app/models/graph/applications/enums/AppRoleAllowedMemberType";
import { ApplicationRegistrationTenantType } from "src/app/models/ApplicationRegistrationTenantType";
import { ChevronApplicationService } from "src/app/services/application/ChevronApplication.service";

@Component({
    selector: 'app-search',
    templateUrl: './app-search.component.html',
    styleUrls: ['./app-search.component.css']
})

export class AppSearchComponent implements OnInit, OnChanges {

    private permissionsPaginator!: MatPaginator;

    constructor(private authService: CalAngularService, private graphAppService: GraphApplicationService,
        private chevronAppService: ChevronApplicationService
    ) { }


    @ViewChild('permissionsPaginator') set matPaginator(mp: MatPaginator) {
        this.permissionsPaginator = mp;
        this.setDataSourceAttributes();
    }
    setDataSourceAttributes() {
        this.permissionsDataSource.paginator = this.permissionsPaginator;
        this.permissionsDataSource.sort = this.sort;

        if (this.permissionsPaginator && this.sort) {
            this.doFilterPermissions('');
        }
    }
    @ViewChild(MatSort) sort!: MatSort;
    @Input() showTitle: boolean = true;
    @Input() isDialog: boolean = false;
    @Input() appRoleAllowedMemberType: AppRoleAllowedMemberType = AppRoleAllowedMemberType.Application;
    @Input() searchPermissions: boolean = true;
    // change search endpoint to one of the search by owners
    @Input() ownedApps: boolean = false;
    @Input() ownedAppsIncludeManagedIdentities: boolean = false;
    // determines whether to search by current user or box for user search
    @Input() allowOwnerSearch: boolean = false;
    @Input() servicePrincipals: boolean = false;
    @Input() servicePrincipalsMustBeManagedIdentity: boolean = false;
    @Input() includeNumberOfExposedPermissions: boolean = false;
    @Input() sortable: boolean = false;
    @Input() makeSticky: boolean = false;
    @Input() tenantType: ApplicationRegistrationTenantType = ApplicationRegistrationTenantType.Chevron;
    @Input() onlyShowApplicationPermissions: boolean = false;

    @Output() appSelected = new EventEmitter<IGraphApplicationPermissions>();
    @Output() searchCancelled = new EventEmitter<{ searchCancelled: boolean }>();
    @Output() searchInProgress = new EventEmitter<boolean>();
    @Output() searchFoundNoExposedPermissions = new EventEmitter<boolean>();

    isSearching: boolean = false;
    screenMessage: string = '';
    objectType: string = '';

    searchTerm = new UntypedFormControl('');
    searchResults: IGraphApplication[] = [];
    displayedColumns: string[] = ['appId', 'displayName', 'serviceId'];

    permissionsDataSource = new MatTableDataSource<IGraphAppExposedPermissions>();
    permissionsDisplayedColumns: string[] = ['select', 'value', 'description', 'type'];

    selectedApp: IGraphApplication = new GraphApplication();
    selection = new SelectionModel<IGraphAppExposedPermissions>(true, []);

    appPermissionDisplayValues = GraphConstants.AppPermissionsDisplayValues;

    appSearchObserver = {
        next: (x: IGenericApiResponse<GraphApplication[]>) => {
            this.searchResults = x.data;
        },
        error: (err: any) => {
            this.screenMessage = err.statusText;
            this.isSearching = false;
            this.searchInProgress.emit(this.isSearching);
        },
        complete: () => {
            this.screenMessage = this.searchResults.length === 0 ? 'no results found' : '';
            this.isSearching = false;
            this.searchInProgress.emit(this.isSearching);
        }
    };

    async ngOnInit(): Promise<void> {
        if (this.includeNumberOfExposedPermissions) {
            this.displayedColumns.push("numberOfExposedPermissions");

            // also include column for Chevron Owned Apps
            this.displayedColumns.push("chevronOwned");
        }

        this.setObjectType();
    }

    ngOnChanges(changes: SimpleChanges): void {

        let shouldReload = false;
        if (changes.servicePrincipals) {
            this.servicePrincipals = changes.servicePrincipals.currentValue;
            this.setObjectType();
            shouldReload = true;
        }

        if (changes.ownedAppsIncludeManagedIdentities) {
            this.ownedAppsIncludeManagedIdentities = changes.ownedAppsIncludeManagedIdentities.currentValue;
            shouldReload = true;
        }

        if (changes.ownedApps) {
            this.ownedApps = changes.ownedApps.currentValue;
            shouldReload = true;
        }

        if (changes.servicePrincipalsMustBeManagedIdentity) {
            this.servicePrincipalsMustBeManagedIdentity = changes.servicePrincipalsMustBeManagedIdentity.currentValue;
            shouldReload = true;
        }

        if (changes.onlyShowApplicationPermissions) {
            this.onlyShowApplicationPermissions = changes.onlyShowApplicationPermissions.currentValue;
            if (this.selectedApp.exposedPermissions.length > 0) {
                this.changePermissionTypeToDisplay();
            }
        }

        if (shouldReload) {
            this.reset();
            this.searchTerm.setValue('');
            this.searchResults = [];
            this.selectedApp = new GraphApplication();
            this.selection.clear();

            if (this.ownedApps && !this.allowOwnerSearch) {
                this.screenMessage = '';
                this.isSearching = true;
                this.searchInProgress.emit(this.isSearching);
                if (this.servicePrincipals) {
                    if (this.servicePrincipalsMustBeManagedIdentity) {
                        this.chevronAppService.GetOwnedUserAssignedManagedIdentities().subscribe(this.appSearchObserver);
                    }
                }
                else {
                    this.graphAppService.GetOwned(undefined, this.tenantType, false, this.ownedAppsIncludeManagedIdentities).subscribe(this.appSearchObserver);
                }
            }
        }
    }

    setObjectType() {
        this.objectType = this.servicePrincipals ? 'service principal' : 'application';
    }

    async search() {
        this.isSearching = true;
        this.searchInProgress.emit(this.isSearching);
        this.screenMessage = ''; //hide the no records found

        let apiCall = this.servicePrincipals ?
            this.servicePrincipalsMustBeManagedIdentity ? this.chevronAppService.GetOwnedUserAssignedManagedIdentities() : this.graphAppService.SearchServicePrincipal(this.searchTerm.value) :
            this.graphAppService.SearchApplication(this.searchTerm.value);

        apiCall.subscribe(this.appSearchObserver);
    }

    async selectApp(selected: IGraphApplication) {
        this.isSearching = true;
        this.searchInProgress.emit(this.isSearching);

        if (this.servicePrincipals && this.servicePrincipalsMustBeManagedIdentity) {
            this.appSelected.emit(new GraphApplicationPermissions(selected, []));
            return;
        }

        const observer = {
            next: (x: IGenericApiResponse<IGraphApplication>) => {
                this.selectedApp = x.data;
            },
            error: (err: any) => {
                this.screenMessage = err.statusText;
                this.isSearching = false;
                this.searchInProgress.emit(this.isSearching);
            },
            complete: () => {
                if (!this.searchPermissions) {
                    this.appSelected.emit(new GraphApplicationPermissions(this.selectedApp, []));
                    this.reset();
                }
                else {
                    if (this.selectedApp.exposedPermissions.length == 0) {
                        // no exposed permissions found after search, emit so components can react
                        this.searchFoundNoExposedPermissions.emit(true);
                    }

                    this.permissionsDataSource.paginator = this.permissionsPaginator;
                    this.isSearching = false;
                    this.searchInProgress.emit(this.isSearching);
                    this.changePermissionTypeToDisplay();
                }
            }
        }

        let apiCall;
        if (!selected.id) {
            apiCall = this.graphAppService.GetServicePrincipal(selected.servicePrincipalId);
        }
        else {
            apiCall = this.servicePrincipals ?
                this.graphAppService.GetServicePrincipal(selected.id) :
                this.graphAppService.GetApplication(selected.id, this.appRoleAllowedMemberType);
        }
        apiCall.subscribe(observer);
    }

    selectPermission(row: IGraphAppExposedPermissions) {
        this.selection.toggle(row);
    }

    changePermissionTypeToDisplay() {
        if (this.onlyShowApplicationPermissions) {
            this.permissionsDataSource.data = this.selectedApp.exposedPermissions.filter(item => item.type === "microsoft.graph.appRole");
        }
        else {
            this.permissionsDataSource.data = this.selectedApp.exposedPermissions;
        }
        //this.cdref.markForCheck();
    }

    public doFilterPermissions = (value: any) => {
        this.permissionsDataSource.filter = value.target.value.trim().toLocaleLowerCase();
    }

    reset() {
        this.searchTerm.setValue('');
        this.searchResults = [];
        this.selectedApp = new GraphApplication();
        this.selection.clear();
    }

    savePermission() {
        this.appSelected.emit(new GraphApplicationPermissions(this.selectedApp, this.selection.selected));
        this.reset();
    }
}